import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Resizable, ResizeCallbackData } from "react-resizable";

import { useEnableConnectWizard } from "featureFlags/hooks/useEnableConnectWizard";

import DocumentObjectExplorerCollapseIcon from "assets/icons/DocumentObjectExplorerCollapseIcon.svg?react";
import DocumentOutputCollpaseIcon from "assets/icons/DocumentOutputCollpaseIcon.svg?react";

import useActiveDocument from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useActiveDocument";
import { SHORTCUTS } from "pages/DevelopWorkspace/services/shortcuts/constants";
import { getShortcutHint } from "pages/DevelopWorkspace/services/shortcuts/helpers/getShortcutHint";
import { ShortcutAction } from "pages/DevelopWorkspace/services/shortcuts/types";

import Button, { ButtonSize, ButtonTemplate } from "components/Button/Button";
import {
  LeftSidebarContext,
  useLeftSidebarState,
} from "components/LeftSidebar/context";
import Tooltip from "components/Tooltip";

import { useLayoutToggles } from "../LayoutToggles/context";
import ConnectModalLoader from "../LeftSidebar/ConnectModal/ConnectModalLoader";
import { Directories } from "./Directories/Directories";
import { Header } from "./Header/Header";
import { LeftSidebarSection } from "./types";

import styles from "./LeftSidebarV2.module.scss";

const LEFT_MAX_SIDEBAR = 500;
const LEFT_MIN_SIDEBAR = 280;
const LEFT_DEFAULT_SIDEBAR = 400;

const LeftSidebarV2 = () => {
  const [isResizing, setIsResizing] = useState(false);

  const [activeSection, setActiveSection] = useState(LeftSidebarSection.DATA);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  const { layout, setLayout } = useLayoutToggles();

  const [width, setWidth] = useState(
    layout.leftSidebar?.width || LEFT_DEFAULT_SIDEBAR
  );

  const { leftSidebarContext } = useLeftSidebarState({ setWidth });

  const activeDocument = useActiveDocument();
  const isConnectWizardEnabled = useEnableConnectWizard();

  const { t } = useTranslation();

  const maxConstraint = LEFT_MAX_SIDEBAR;
  const minConstraint = LEFT_MIN_SIDEBAR;

  const handleResize = (
    event: React.SyntheticEvent,
    { size }: ResizeCallbackData
  ) => {
    if (!isResizing) {
      setIsResizing(true);
    }
    setWidth(size.width);
  };

  const handleResizeStop = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    if (isResizing) {
      setIsResizing(false);
    }
    setLayout({
      ...layout,
      leftSidebar: {
        ...layout.leftSidebar,
        width: data.size.width,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <Resizable
        className={classNames(styles.drawer, {
          [styles.isResizing]: isResizing,
          [styles.hidden]: !layout.leftSidebar.expanded,
        })}
        width={width}
        height={window.innerHeight}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        minConstraints={[minConstraint, LEFT_DEFAULT_SIDEBAR]}
        maxConstraints={[maxConstraint, LEFT_MAX_SIDEBAR]}
        resizeHandles={["e"]}
        handle={
          <span
            data-testid="aqa-resize-handle-left"
            className={classNames(styles.customHandle)}
          />
        }
      >
        <div style={{ width }}>
          <LeftSidebarContext.Provider value={leftSidebarContext}>
            <Header
              activeSection={activeSection}
              setActiveSection={setActiveSection}
            />
            <Directories
              activeSection={activeSection}
              key={activeSection}
            />

            <div
              className={classNames(styles.sidebarFooter, {
                [styles.expanded]: layout.leftSidebar.expanded,
              })}
            >
              <div
                className={classNames(styles.layoutToggles, {
                  [styles.expanded]: layout.leftSidebar.expanded,
                })}
              >
                <Tooltip
                  title={
                    layout.leftSidebar.expanded
                      ? t("workspace.tooltips.hide_sidebar")
                      : t("workspace.tooltips.show_sidebar")
                  }
                  placement="top-start"
                >
                  <div
                    className={classNames(styles.toggle, {
                      [styles.active]: layout.leftSidebar.expanded,
                    })}
                    onClick={() => {
                      setLayout({
                        ...layout,
                        leftSidebar: {
                          ...layout.leftSidebar,
                          expanded: !layout.leftSidebar.expanded,
                        },
                      });
                    }}
                    data-testid="toggle-workspace-left-sidebar"
                  >
                    <DocumentObjectExplorerCollapseIcon />
                  </div>
                </Tooltip>
                <Tooltip
                  title={`${
                    layout.results.expanded
                      ? t("workspace.tooltips.hide_results")
                      : t("workspace.tooltips.show_results")
                  } (${getShortcutHint(
                    SHORTCUTS[ShortcutAction.ToggleResultsSection]
                  ).join(" + ")})`}
                  placement="top-start"
                >
                  <div
                    className={classNames(styles.toggle, {
                      [styles.active]: layout.results.expanded,
                    })}
                    data-testid="toggle-workspace-results"
                    onClick={() => {
                      setLayout({
                        ...layout,
                        results: {
                          ...layout.results,
                          expanded: !layout.results.expanded,
                        },
                      });
                    }}
                  >
                    <DocumentOutputCollpaseIcon />
                  </div>
                </Tooltip>
              </div>

              {isConnectWizardEnabled && (
                <div className={styles.connectButton}>
                  <Tooltip
                    title={t("connect_modal.button_tooltip")}
                    placement="top-start"
                  >
                    <div>
                      <Button
                        text={t("connect_modal.button_text")}
                        template={ButtonTemplate.Tertiary}
                        size={ButtonSize.Small}
                        onClick={() => setIsConnectModalOpen(true)}
                        dataTestid="connect-button"
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </LeftSidebarContext.Provider>
        </div>
      </Resizable>
      {isConnectModalOpen && (
        <ConnectModalLoader
          onClose={() => setIsConnectModalOpen(false)}
          defaultDatabase={
            activeDocument?.context?.database?.name &&
            typeof activeDocument.context.database.name === "string"
              ? activeDocument.context.database.name
              : undefined
          }
          defaultEngine={
            activeDocument?.context?.engine?.name &&
            typeof activeDocument.context.engine.name === "string"
              ? activeDocument.context.engine.name
              : undefined
          }
        />
      )}
    </div>
  );
};

export default LeftSidebarV2;
